import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ───────────────────────────────────────────────────────────────

export type EntityType = "asset" | "part";

export interface EntityImage {
  id: string;
  entity_id: string;   // asset_id or part_id
  org_id: string;
  storage_path: string;
  public_url: string;
  thumbnail_url: string; // derived: _thumb variant of public_url
  is_primary: boolean;
  created_at: string;
}

// ── Image helpers ────────────────────────────────────────────────────────

/**
 * Compress an image file to a JPEG of at most `maxWidth` pixels wide,
 * at the given quality (0–1). Aspect ratio is preserved.
 */
function compressImage(file: File, maxWidth: number, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, maxWidth / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("Canvas toBlob failed")),
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("Image load failed")); };
    img.src = objectUrl;
  });
}

/** Inserts `_thumb` before the file extension in a storage path or public URL. */
function toThumbSuffix(path: string): string {
  return path.replace(/(\.[^./?#]+)(\?.*)?$/, "_thumb$1$2");
}

const TABLE: Record<EntityType, string> = {
  asset: "entity_image",
  part: "entity_image",
};

const ID_COL: Record<EntityType, string> = {
  asset: "entity_id",
  part: "entity_id",
};

const BUCKET: Record<EntityType, string> = {
  asset: "asset-photos",
  part: "part-photos",
};

// ── Query ────────────────────────────────────────────────────────────────

export function useEntityImages(entityType: EntityType, entityId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["entity-images", entityType, entityId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLE[entityType] as any)
        .select("*")
        .eq(ID_COL[entityType], entityId!)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Normalise: add entity_id and derived thumbnail_url
      return ((data || []) as any[]).map((row) => ({
        ...row,
        entity_id: row[ID_COL[entityType]],
        thumbnail_url: toThumbSuffix(row.public_url),
      })) as EntityImage[];
    },
    enabled: !!entityId && isAuthenticated && !authLoading,
  });
}

// ── Upload ───────────────────────────────────────────────────────────────

export function useUploadEntityImage(entityType: EntityType) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      entityId,
      orgId,
    }: {
      file: File;
      entityId: string;
      orgId: string;
    }) => {
      // 1. Compress to full (1200px) and thumbnail (300px) before uploading.
      //    Falls back to the original file if the browser can't decode the format (e.g. HEIC).
      let fullBlob: Blob;
      let thumbBlob: Blob;
      let isCompressed = true;
      try {
        [fullBlob, thumbBlob] = await Promise.all([
          compressImage(file, 1200, 0.85),
          compressImage(file, 300, 0.80),
        ]);
      } catch {
        fullBlob = file;
        thumbBlob = file;
        isCompressed = false;
      }

      const ts = Date.now();
      const ext = isCompressed ? "jpg" : (file.name.split(".").pop() ?? "jpg");
      const contentType = isCompressed ? "image/jpeg" : file.type;
      const storagePath = `${orgId}/${entityType}/${entityId}/${ts}.${ext}`;
      const thumbPath = toThumbSuffix(storagePath);

      const [{ error: uploadError }, { error: thumbError }] = await Promise.all([
        supabase.storage.from(BUCKET[entityType]).upload(storagePath, fullBlob, { contentType, cacheControl: "3600", upsert: false }),
        supabase.storage.from(BUCKET[entityType]).upload(thumbPath, thumbBlob, { contentType, cacheControl: "3600", upsert: false }),
      ]);

      if (uploadError) throw uploadError;
      if (thumbError) throw thumbError;

      const { data: urlData } = supabase.storage
        .from(BUCKET[entityType])
        .getPublicUrl(storagePath);

      // 2. Check if this is the first image (auto-set as primary)
      const { count } = await supabase
        .from(TABLE[entityType] as any)
        .select("*", { count: "exact", head: true })
        .eq(ID_COL[entityType], entityId);

      const isPrimary = (count ?? 0) === 0;

      // 3. Insert image record
      const { data, error: insertError } = await supabase
        .from(TABLE[entityType] as any)
        .insert([
          {
            [ID_COL[entityType]]: entityId,
            org_id: orgId,
            storage_path: storagePath,
            public_url: urlData.publicUrl,
            is_primary: isPrimary,
          },
        ])
        .select()
        .single();

      if (insertError) throw insertError;

      // 4. If primary, also update image_url on the parent record
      if (isPrimary) {
        await supabase
          .from(entityType === "asset" ? ("asset" as any) : ("part" as any))
          .update({ image_url: urlData.publicUrl })
          .eq("id", entityId);
      }

      return { ...(data as any), entity_id: (data as any)[ID_COL[entityType]] } as EntityImage;
    },
    onSuccess: (_data, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: ["entity-images", entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: [entityType === "asset" ? "assets" : "parts"] });
      toast({ title: "Image uploaded" });
    },
    onError: (error: Error) => {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    },
  });
}

// ── Set Primary ──────────────────────────────────────────────────────────

export function useSetPrimaryImage(entityType: EntityType) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      imageId,
      entityId,
      publicUrl,
    }: {
      imageId: string;
      entityId: string;
      publicUrl: string;
    }) => {
      // Clear all primary flags for this entity
      const { error: clearError } = await supabase
        .from(TABLE[entityType] as any)
        .update({ is_primary: false })
        .eq(ID_COL[entityType], entityId);

      if (clearError) throw clearError;

      // Set the chosen image as primary
      const { error: setError } = await supabase
        .from(TABLE[entityType] as any)
        .update({ is_primary: true })
        .eq("id", imageId);

      if (setError) throw setError;

      // Update parent record image_url
      await supabase
        .from(entityType === "asset" ? ("asset" as any) : ("part" as any))
        .update({ image_url: publicUrl })
        .eq("id", entityId);
    },
    onSuccess: (_data, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: ["entity-images", entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: [entityType === "asset" ? "assets" : "parts"] });
      toast({ title: "Main image updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

// ── Delete ───────────────────────────────────────────────────────────────

export function useDeleteEntityImage(entityType: EntityType) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      image,
      entityId,
      allImages,
    }: {
      image: EntityImage;
      entityId: string;
      allImages: EntityImage[];
    }) => {
      // Delete full image and thumbnail from storage
      await supabase.storage.from(BUCKET[entityType]).remove([
        image.storage_path,
        toThumbSuffix(image.storage_path),
      ]);

      // Delete DB record
      const { error } = await supabase
        .from(TABLE[entityType] as any)
        .delete()
        .eq("id", image.id);

      if (error) throw error;

      // If deleted image was primary, promote the oldest remaining one
      if (image.is_primary) {
        const next = allImages.find((img) => img.id !== image.id);
        if (next) {
          await supabase
            .from(TABLE[entityType] as any)
            .update({ is_primary: true })
            .eq("id", next.id);

          await supabase
            .from(entityType === "asset" ? ("asset" as any) : ("part" as any))
            .update({ image_url: next.public_url })
            .eq("id", entityId);
        } else {
          // No images left — clear image_url
          await supabase
            .from(entityType === "asset" ? ("asset" as any) : ("part" as any))
            .update({ image_url: null })
            .eq("id", entityId);
        }
      }
    },
    onSuccess: (_data, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: ["entity-images", entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: [entityType === "asset" ? "assets" : "parts"] });
      toast({ title: "Image deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error deleting image", description: error.message, variant: "destructive" });
    },
  });
}
